import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { LANGUAGES, type LanguageCode } from '@/i18n';

export const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = (() => {
    const lng = i18n.language || i18n.resolvedLanguage || 'de';
    return LANGUAGES.find((l) => lng === l.code || lng.startsWith(l.code + '-')) ?? LANGUAGES[0];
  })();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (code: LanguageCode) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  const isSelected = (code: string) => {
    const lng = i18n.language || i18n.resolvedLanguage || '';
    return lng === code || lng.startsWith(code + '-');
  };

  const dropdown = isOpen && buttonRef.current && (
    (() => {
      const rect = buttonRef.current.getBoundingClientRect();
      return createPortal(
        <div
          ref={dropdownRef}
          className="fixed py-2 rounded-xl glass border border-white/10 shadow-xl min-w-[180px] z-[200] max-h-[70vh] overflow-y-auto"
          role="listbox"
          style={{
            top: rect.bottom + 8,
            left: Math.max(12, rect.left + rect.width / 2 - 90),
          }}
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={isSelected(lang.code)}
              onClick={() => handleSelect(lang.code)}
              className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                isSelected(lang.code)
                  ? 'bg-accent-pink/20 text-accent-pink font-medium'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>,
        document.body
      );
    })()
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl glass hover:bg-white/10 transition-colors text-sm"
        aria-label="Sprache wählen"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4 text-text-muted" />
        <span className="text-white font-medium max-w-[100px] truncate">
          {currentLang.label}
        </span>
        <svg
          className={`w-4 h-4 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {dropdown}
    </>
  );
};
